import { API_GQ } from '../helpers/apiConfig';
import { GET_REASON_MASTER_DATA } from '../queries/common';
import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';
import { STORAGE_KEYS } from '../constants/common';
import { useAuthProvider } from './AuthProvider';

interface Props {
  children: ReactNode;
}

export interface ReasonMasterGroup {
  groupId: string;
  groupNameEn: string;
  groupNameFr: string;
}

export interface ReasonMasterReason {
  reasonId: string;
  reasonTitleEn: string;
  reasonTitleFr: string;
  groupIds: string[];
  shorthandTxt: string;
  compensation: string | string[];
}

export interface ReasonMasterCabin {
  cabinId: string;
  cabinTitleEn: string;
  cabinTitleFr: string;
}

export interface ReasonMasterCategory {
  categoryId: string;
  reasonId: string;
  categoryTitleEn: string;
  categoryTitleFr: string;
  shorthandTxt: string;
  groupIds: string[];
  compensation: string | string[];
  cabins?: Array<{
    cabinId: string;
    shorthandTxt?: string;
    compensation: string | string[];
  }>;
}

export interface ReasonMasterData {
  groups: ReasonMasterGroup[];
  reasons: ReasonMasterReason[];
  cabins: ReasonMasterCabin[];
  categories: ReasonMasterCategory[];
}

const getEmptyMasterData = (): ReasonMasterData => {
  return { groups: [], reasons: [], cabins: [], categories: [] };
};

const normalizeMasterData = (data: ReasonMasterData | null | undefined): ReasonMasterData => {
  if (!data) return getEmptyMasterData();
  return {
    groups: Array.isArray(data.groups) ? data.groups : [],
    reasons: Array.isArray(data.reasons) ? data.reasons : [],
    cabins: Array.isArray(data.cabins) ? data.cabins : [],
    categories: Array.isArray(data.categories) ? data.categories : []
  };
};

// Merge any locally cached category->cabin mappings into freshly loaded data
const applyCategoryOverrides = (data: ReasonMasterData): ReasonMasterData => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.reasonMasterData);
    if (!raw) return data;
    const cached = JSON.parse(raw);
    const cachedCategories: any[] = Array.isArray(cached?.categories) ? cached.categories : [];
    if (!cachedCategories.length || !Array.isArray(data.categories)) return data;

    const cachedById = new Map<string, any>();
    cachedCategories.forEach((c: any) => {
      if (c && typeof c.categoryId === 'string') {
        cachedById.set(c.categoryId, c);
      }
    });

    // Helper to derive groupIds from parent reason when missing
    const getReasonGroupIds = (reasonId?: string): string[] => {
      if (!reasonId) return [];
      const r = (Array.isArray(data.reasons) ? data.reasons : []).find(x => x.reasonId === reasonId);
      return Array.isArray(r?.groupIds) ? r!.groupIds : [];
    };

    // Start with fetched categories and merge cached fields
    const mergedById = new Map<string, any>();
    data.categories.forEach(cat => {
      const cachedCat = cachedById.get(cat.categoryId);
      if (cachedCat) {
        const merged = {
          ...cat,
          // prefer fetched titles but bring over missing fields
          groupIds: (Array.isArray(cat.groupIds) && cat.groupIds.length)
            ? cat.groupIds
            : (Array.isArray(cachedCat.groupIds) && cachedCat.groupIds.length
              ? cachedCat.groupIds
              : getReasonGroupIds(cat.reasonId)),
          shorthandTxt: typeof cat.shorthandTxt === 'string' && cat.shorthandTxt !== '' ? cat.shorthandTxt : (cachedCat.shorthandTxt || ''),
          // bring over cabins mapping if available in cache
          cabins: Array.isArray(cachedCat.cabins) && cachedCat.cabins.length ? cachedCat.cabins : cat.cabins
        };
        mergedById.set(merged.categoryId, merged);
      } else {
        const filled = {
          ...cat,
          groupIds: (Array.isArray(cat.groupIds) && cat.groupIds.length) ? cat.groupIds : getReasonGroupIds(cat.reasonId)
        };
        mergedById.set(filled.categoryId, filled);
      }
    });

    // Add cached-only categories (not present in fetched data)
    cachedCategories.forEach(cachedCat => {
      if (cachedCat && typeof cachedCat.categoryId === 'string' && !mergedById.has(cachedCat.categoryId)) {
        const filled = {
          ...cachedCat,
          groupIds: (Array.isArray(cachedCat.groupIds) && cachedCat.groupIds.length) ? cachedCat.groupIds : getReasonGroupIds(cachedCat.reasonId)
        };
        mergedById.set(filled.categoryId, filled);
      }
    });

    const mergedCategories = Array.from(mergedById.values());

    return { ...data, categories: mergedCategories };
  } catch {
    return data;
  }
};

const MasterDataContext = createContext({
  masterData: getEmptyMasterData(),
  refreshMasterData: async () => {}
});

export const MasterDataProvider = ({ children }: Props) => {
  const authContext = useAuthProvider();
  const [masterData, setMasterData] = useState<ReasonMasterData>(getEmptyMasterData());

  const saveMasterDataToStorage = (data: ReasonMasterData) => {
    localStorage.setItem(STORAGE_KEYS.reasonMasterData, JSON.stringify(data));
  };

  const loadMasterDataFromStorage = (): ReasonMasterData | null => {
    const raw = localStorage.getItem(STORAGE_KEYS.reasonMasterData);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      const normalized = normalizeMasterData(parsed);
      return applyCategoryOverrides(normalized);
    } catch {
      return null;
    }
  };

  const refreshMasterData = useCallback(async () => {
    try {
      const accessToken = await authContext.acquireTokenSilently();
      if (!accessToken) return;
      const body = {
        query: GET_REASON_MASTER_DATA,
        variables: {}
      };
      const response = await API_GQ.post(accessToken, body);
      const json = await response.json();
      const remoteData = json?.data?.getReasonMasterData;
      let payload = normalizeMasterData(remoteData?.data);
      // Apply overrides to ensure correct mapping for known categories
      payload = applyCategoryOverrides(payload);
      const statusCode = remoteData?.code;
      const isSuccessful = statusCode === undefined
        || statusCode === null
        || statusCode === 200
        || statusCode === '200'
        || statusCode === 'SUCCESS'
        || statusCode === 'OK';
      if (!isSuccessful && !payload.groups.length && !payload.reasons.length && !payload.cabins.length && !payload.categories.length) {
        return;
      }
      setMasterData(payload);
      saveMasterDataToStorage(payload);
    } catch {
      // ignore errors and rely on cached data when available
    }
  }, [authContext]);

  useEffect(() => {
    if (!authContext.user?.accessToken) return;
    refreshMasterData();
  }, [authContext.user?.accessToken, refreshMasterData]);

  useEffect(() => {
    const cached = loadMasterDataFromStorage();
    if (cached) {
      setMasterData(cached);
      return;
    }
    refreshMasterData();
  }, [refreshMasterData]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEYS.reasonMasterData) return;
      if (event.newValue) {
        try {
          const parsed = JSON.parse(event.newValue);
          const normalized = normalizeMasterData(parsed);
          setMasterData(applyCategoryOverrides(normalized));
        } catch {
          refreshMasterData();
        }
        return;
      }
      refreshMasterData();
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [refreshMasterData]);

  useEffect(() => {
    const handleFocus = () => {
      const cached = loadMasterDataFromStorage();
      if (cached) {
        setMasterData(cached);
        return;
      }
      refreshMasterData();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refreshMasterData]);

  return (
    <MasterDataContext.Provider value={{ masterData, refreshMasterData }}>
      {children}
    </MasterDataContext.Provider>
  );
};

export const useMasterData = () => {
  return useContext(MasterDataContext);
};

